<script lang="ts" module>
  import AppLayout from '$lib/layouts/AppLayout.svelte'
  export const layout = AppLayout
</script>

<script lang="ts">
  import { inertia, router } from "@inertiajs/svelte";
  import * as Select from "$lib/components/ui/select/index.js";
  import { 
    Mail, 
    Phone, 
    Building, 
    MessageSquare, 
    Globe, 
    Tag, 
    CheckCircle2, 
    Clock, 
    User,
    ChevronRight,
    X,
    Trash2
  } from "lucide-svelte";
  import { appStore } from '$lib/stores/appStore';

  interface InquiryItem {
    id: string;
    name: string;
    phone: string;
    email: string;
    company: string;
    message: string;
    product_id: string;
    locale: string;
    status: number;
    created_at: string;
    date_time: string;
  }

  // Svelte 5 $props() 接收后端传递的数据及多语言词条对象
  let { 
    inquiries = [], 
    current_status = "all", 
    counts = { all: 0, pending: 0, contacted: 0, completed: 0 },
    t = {} as any
  } = $props();

  // 状态管理状态：当前选中的查看详情线索
  let selectedInquiry: InquiryItem | null = $state(null);

  // 切换列表状态 Tab
  function filterStatus(status: string) {
    router.get('/inquiries', { status: status === 'all' ? '' : status }, { preserveState: true });
  }

  // 改变询盘状态（待处理 0 / 已联系 1 / 已完成 2）
  function updateStatus(id: string, newStatus: number) {
    router.patch(`/inquiries/${id}`, { inquiry: { status: newStatus } }, {
      preserveState: true,
      onSuccess: () => {
        // 如果侧边栏正打开着，同步更新侧边栏内部的状态
        if (selectedInquiry && selectedInquiry.id === id) {
          selectedInquiry.status = newStatus;
        }
      }
    });
  }

  // 删除/归档询盘
  function deleteInquiry(id: string) {
    if (!confirm(t.drawer?.confirm_delete || "确定要删除这条客户线索吗？")) return;
    router.delete(`/inquiries/${id}`, {
      preserveState: true,
      onSuccess: () => selectedInquiry = null
    });
  }


  $effect(() => {
    const title = t.title;
    appStore.setTitle(title);
  });
</script>

<svelte:head>
  <title>{t.title} | 分太极光</title>
</svelte:head>

<div class="w-full p-1 relative min-h-[calc(100vh-8rem)]">
  
  <div class="mb-6">
    <h1 class="text-2xl font-bold tracking-tight">{t.title}</h1>
    <p class="text-sm text-muted-foreground mt-1">
      {t.subtitle}
    </p>
  </div>

  <div class="flex flex-wrap gap-2 mb-6 border-b pb-4">
    <button 
      onclick={() => filterStatus('all')}
      class="px-4 py-2 text-sm font-medium rounded-md transition-all flex items-center gap-2
        {current_status === 'all' ? 'bg-primary text-primary-foreground shadow' : 'bg-muted/50 hover:bg-muted text-muted-foreground'}"
    >
      {t.tabs?.all} <span class="text-xs px-1.5 py-0.5 rounded-full bg-background/20">{counts.all}</span>
    </button>

    <button 
      onclick={() => filterStatus('0')}
      class="px-4 py-2 text-sm font-medium rounded-md transition-all flex items-center gap-2
        {current_status === '0' ? 'bg-amber-500 text-white shadow' : 'bg-amber-500/10 hover:bg-amber-500/20 text-amber-600'}"
    >
      <Clock class="size-3.5" />
      {t.tabs?.pending} <span class="text-xs px-1.5 py-0.5 rounded-full bg-background/30">{counts.pending}</span>
    </button>

    <button 
      onclick={() => filterStatus('1')}
      class="px-4 py-2 text-sm font-medium rounded-md transition-all flex items-center gap-2
        {current_status === '1' ? 'bg-violet-600 text-white shadow' : 'bg-violet-600/10 hover:bg-violet-600/20 text-violet-600'}"
    >
      <MessageSquare class="size-3.5" />
      {t.tabs?.contacted} <span class="text-xs px-1.5 py-0.5 rounded-full bg-background/30">{counts.contacted}</span>
    </button>

    <button 
      onclick={() => filterStatus('2')}
      class="px-4 py-2 text-sm font-medium rounded-md transition-all flex items-center gap-2
        {current_status === '2' ? 'bg-emerald-600 text-white shadow' : 'bg-emerald-600/10 hover:bg-emerald-600/20 text-emerald-600'}"
    >
      <CheckCircle2 class="size-3.5" />
      {t.tabs?.completed} <span class="text-xs px-1.5 py-0.5 rounded-full bg-background/30">{counts.completed}</span>
    </button>
  </div>

  <div class="rounded-xl border bg-card shadow-sm overflow-hidden">
    {#if inquiries.length === 0}
      <div class="flex flex-col items-center justify-center py-16 px-4 text-center">
        <div class="p-4 bg-muted rounded-full text-muted-foreground mb-3">
          <Mail class="size-8" />
        </div>
        <h3 class="text-base font-semibold">{t.table?.empty_title}</h3>
        <p class="text-xs text-muted-foreground mt-1 max-w-sm">
          {t.table?.empty_desc}
        </p>
      </div>
    {:else}
      <div class="overflow-x-auto">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr class="border-b bg-muted/30 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              <th class="p-4">{t.table?.customer_info}</th>
              <th class="p-4">{t.table?.summary}</th>
              <th class="p-4">{t.table?.locale_project}</th>
              <th class="p-4">{t.table?.status}</th>
              <th class="p-4">{t.table?.submitted_at}</th>
              <th class="p-4 text-right">{t.table?.actions}</th>
            </tr>
          </thead>
          <tbody class="divide-y text-sm">
            {#each inquiries as item (item.id)}
              <tr 
                class="hover:bg-muted/40 transition-colors cursor-pointer {selectedInquiry?.id === item.id ? 'bg-primary/5' : ''}"
                onclick={() => selectedInquiry = item}
              >
                <td class="p-4">
                  <div class="flex flex-col">
                    <span class="font-semibold text-foreground flex items-center gap-1.5">
                      <User class="size-3.5 text-muted-foreground" /> {item.name || t.table?.anonymous}
                    </span>
                    {#if item.company}
                      <span class="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                        <Building class="size-3" /> {item.company}
                      </span>
                    {/if}
                  </div>
                </td>

                <td class="p-4 max-w-xs md:max-w-md">
                  <p class="text-muted-foreground truncate">{item.message || t.table?.no_message}</p>
                </td>

                <td class="p-4">
                  <div class="flex flex-col gap-0.5">
                    <span class="text-xs font-medium inline-flex items-center gap-1 text-sky-600 uppercase">
                      <Globe class="size-3" /> {item.locale || 'en'}
                    </span>
                    {#if item.product_id}
                      <span class="text-[11px] text-muted-foreground truncate">
                        ID: {item.product_id}
                      </span>
                    {/if}
                  </div>
                </td>

                <td class="p-4" onclick={(e) => e.stopPropagation()}>
                    <Select.Root 
                        type="single"
                        value={String(item.status)} 
                        onValueChange={(val) => updateStatus(item.id, parseInt(val))}
                    >
                        <Select.Trigger 
                        class="h-7 w-28 text-xs font-medium rounded-full border px-3 transition-colors focus:ring-0 focus:outline-hidden
                            {item.status === 0 ? 'bg-amber-50 text-amber-600 border-amber-200 hover:bg-amber-100/60' : ''}
                            {item.status === 1 ? 'bg-violet-50 text-violet-600 border-violet-200 hover:bg-violet-100/60' : ''}
                            {item.status === 2 ? 'bg-emerald-50 text-emerald-600 border-emerald-200 hover:bg-emerald-100/60' : ''}"
                        >
                        {#if item.status === 0}{t.tabs?.pending}{/if}
                        {#if item.status === 1}{t.tabs?.contacted}{/if}
                        {#if item.status === 2}{t.tabs?.completed}{/if}
                        </Select.Trigger>
                        
                        <Select.Content 
                        class="text-xs min-w-28 shadow-md"
                        onclick={(e) => e.stopPropagation()}
                        >
                        <Select.Item value="0" class="cursor-pointer text-amber-600 focus:bg-amber-50">{t.tabs?.pending}</Select.Item>
                        <Select.Item value="1" class="cursor-pointer text-violet-600 focus:bg-violet-50">{t.tabs?.contacted}</Select.Item>
                        <Select.Item value="2" class="cursor-pointer text-emerald-600 focus:bg-emerald-50">{t.tabs?.completed}</Select.Item>
                        </Select.Content>
                    </Select.Root>
                </td>

                <td class="p-4 text-xs text-muted-foreground whitespace-nowrap">
                  <div class="flex flex-col">
                    <span>{item.created_at}</span>
                    <span class="text-[10px] text-zinc-400 mt-0.5">{item.date_time}</span>
                  </div>
                </td>

                <td class="p-4 text-right">
                  <button class="text-muted-foreground p-1 hover:text-primary transition-colors">
                    <ChevronRight class="size-4" />
                  </button>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  </div>

  {#if selectedInquiry}
    <!-- <div class="fixed inset-0 bg-background/40 backdrop-blur-xs z-40" onclick={() => selectedInquiry = null}></div> -->
     <a class="fixed inset-0 bg-background/40 backdrop-blur-xs z-40" onclick={() => selectedInquiry = null}></a>
    
    <div class="fixed inset-y-0 right-0 w-full max-w-md bg-card border-l shadow-2xl z-50 p-6 flex flex-col transform transition-transform duration-300">
      
      <div class="flex items-center justify-between pb-4 border-b">
        <div>
          <h2 class="text-base font-bold tracking-tight">{t.drawer?.title}</h2>
          <p class="text-xs text-muted-foreground mt-0.5">ID: {selectedInquiry.id}</p>
        </div>
        <button onclick={() => selectedInquiry = null} class="p-1 rounded-md hover:bg-muted text-muted-foreground">
          <X class="size-4" />
        </button>
      </div>

      <div class="flex-1 overflow-y-auto py-4 space-y-5">
        <div class="bg-muted/30 p-4 rounded-xl border">
          <div class="text-lg font-bold text-foreground flex items-center gap-2">
            <User class="size-4 text-primary" /> {selectedInquiry.name || t.table?.anonymous}
          </div>
          {#if selectedInquiry.company}
            <div class="text-sm text-muted-foreground flex items-center gap-1.5 mt-1">
              <Building class="size-3.5" /> {selectedInquiry.company}
            </div>
          {/if}
        </div>

        <div class="space-y-3">
          <h3 class="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t.drawer?.channels}</h3>
          
          <div class="flex items-center justify-between p-2.5 rounded-lg border text-sm bg-background">
            <div class="flex items-center gap-2 truncate">
              <Phone class="size-3.5 text-zinc-400 shrink-0" />
              <span class="font-mono truncate">{selectedInquiry.phone}</span>
            </div>
            <button 
              onclick={() => { navigator.clipboard.writeText(selectedInquiry!.phone); alert(t.drawer?.copied_phone); }}
              class="text-xs text-primary font-medium hover:underline shrink-0 ml-2"
            >
              {t.drawer?.copy}
            </button>
          </div>

          {#if selectedInquiry.email}
            <div class="flex items-center justify-between p-2.5 rounded-lg border text-sm bg-background">
              <div class="flex items-center gap-2 truncate">
                <Mail class="size-3.5 text-zinc-400 shrink-0" />
                <span class="font-mono truncate">{selectedInquiry.email}</span>
              </div>
              <button 
                onclick={() => { navigator.clipboard.writeText(selectedInquiry!.email); alert(t.drawer?.copied_email); }}
                class="text-xs text-primary font-medium hover:underline shrink-0 ml-2"
              >
                {t.drawer?.copy}
              </button>
            </div>
          {/if}
        </div>

        <div class="grid grid-cols-2 gap-3 bg-zinc-50/50 p-3 rounded-lg text-xs border">
          <div>
            <span class="text-muted-foreground block">{t.drawer?.buyer_locale}</span>
            <span class="font-semibold uppercase text-sky-600 flex items-center gap-1 mt-0.5">
              <Globe class="size-3" /> {selectedInquiry.locale}
            </span>
          </div>
          <div>
            <span class="text-muted-foreground block">{t.drawer?.project}</span>
            <span class="font-semibold text-foreground flex items-center gap-1 mt-0.5 truncate">
              <Tag class="size-3 text-zinc-400" /> {selectedInquiry.product_id || t.drawer?.general_form}
            </span>
          </div>
        </div>

        <div class="space-y-1.5">
          <h3 class="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t.drawer?.raw_message}</h3>
          <div class="p-3 bg-muted/40 rounded-xl border text-sm text-foreground whitespace-pre-wrap leading-relaxed min-h-24 font-sans">
            {selectedInquiry.message || t.drawer?.no_message_placeholder}
          </div>
        </div>
      </div>

      <div class="pt-4 border-t flex items-center justify-between gap-2">
        <button 
          onclick={() => deleteInquiry(selectedInquiry!.id)}
          class="p-2 text-destructive hover:bg-destructive/10 rounded-md transition-colors"
          title={t.table?.actions}
        >
          <Trash2 class="size-4" />
        </button>

        <div class="flex items-center gap-2 flex-1 justify-end">
          <button 
            onclick={() => { updateStatus(selectedInquiry!.id, 1); }}
            class="px-3 py-1.5 text-xs font-medium rounded-md border hover:bg-muted transition-colors {selectedInquiry.status === 1 ? 'bg-violet-50 border-violet-200 text-violet-700' : ''}"
          >
            {t.drawer?.btn_contacted}
          </button>
          <button 
            onclick={() => { updateStatus(selectedInquiry!.id, 2); }}
            class="px-3 py-1.5 text-xs font-medium rounded-md bg-primary text-primary-foreground shadow hover:bg-primary/90 transition-colors"
          >
            {t.drawer?.btn_completed}
          </button>
        </div>
      </div>

    </div>
  {/if}

</div>